import { useDisclosure } from '@mantine/hooks';
import { useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import { MRT_RowSelectionState } from 'mantine-react-table';
import { useCallback, useEffect, useState } from 'react';

import { IntercultureForm } from '../../components/interculture-form/interculture-form';
import { IntercultureListHeader } from '../../components/interculture-list-header/interculture-list-header';
import { IntercultureAdviceTable } from '../../components/interculture-table/interculture-table';
import { intercultureApi } from '../../data-access/apis/interculture';
import { intercultureQueries } from '../../data-access/queries/interculture';

import {
  FORM_STATUS,
  useColFilters,
  useQueriesWithGlobal,
  useSharedQueries,
} from '@data-access';
import { FullMainHeight } from '@ui';

export function IntercultureListPage() {
  const { type, selectedForm, editMode } = useSearch({
    from: '/_app/interculture',
  });
  const queries = useQueriesWithGlobal(intercultureQueries);
  const nitrogenTypes = useSuspenseQuery(queries.types());

  const [selectedType, setSelectedType] = useState<string>(
    type ?? nitrogenTypes.data?.[0].name,
  );
  const [filters] = useColFilters('interculture');
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>(
    editMode ? { [Number(selectedForm)]: true } : {},
  );
  const [computationRequestId, setComputationRequestId] = useState<
    number | null
  >(null);

  const selectedRowsIds = Object.keys(rowSelection).map((key) => Number(key));

  const [opened, { open, close }] = useDisclosure(editMode || false);

  const exportSynthesis = useMutation({
    mutationFn: intercultureApi.exportSynthesis,
    onSuccess: (data) => {
      setComputationRequestId(data.instance_id);
    },
  });
  const downloadSynthesis = useMutation({
    mutationFn: intercultureApi.downloadExport,
  });
  const sharedQueries = useSharedQueries();
  const computationRequest = useQuery({
    ...sharedQueries.computationRequest({ id: computationRequestId! }),
    enabled: computationRequestId !== null,
  });
  const handleEditAdvice = useCallback(
    (id?: number) => {
      if (id) {
        setRowSelection({ [id]: true });
      }
      open();
    },

    [open],
  );

  const handleDownloadRecaps = () => {
    if (rowSelection.all) {
      exportSynthesis.mutate({
        ids: [],
        all: true,
        selectedFilters: filters,
        crop: selectedType,
      });
    } else {
      const ids = Object.keys(rowSelection).map((id) => Number(id));
      exportSynthesis.mutate({
        ids,
        crop: selectedType,
      });
    }
  };

  useEffect(() => {
    if (computationRequest.data?.status === FORM_STATUS.FINISHED) {
      downloadSynthesis.mutate({
        id: computationRequest.data.id,
        crop: selectedType,
      });
      setComputationRequestId(null);
    }
  }, [computationRequest.data, downloadSynthesis, selectedType]);

  return (
    <FullMainHeight.Root>
      <FullMainHeight.Header>
        <IntercultureListHeader
          downloadPending={
            computationRequest.data?.status === FORM_STATUS.PENDING
          }
          onCropChange={(e) => setSelectedType(e!)}
          onDownload={handleDownloadRecaps}
          onEditAdvice={handleEditAdvice}
          selectedType={selectedType}
          selecting={selectedRowsIds.length > 0}
        />
      </FullMainHeight.Header>
      <FullMainHeight.Content>
        <IntercultureAdviceTable
          onEdit={handleEditAdvice}
          rowSelection={rowSelection}
          selectedType={selectedType}
          setRowSelection={setRowSelection}
        />

        <IntercultureForm
          key={selectedRowsIds.join(',')}
          onClose={() => {
            setRowSelection({});
            close();
          }}
          opened={opened}
          selectedForm={
            selectedRowsIds.length === 1 ? selectedRowsIds[0] : undefined
          }
          selectedForms={
            selectedRowsIds.length > 1 ? selectedRowsIds : undefined
          }
          selectedType={selectedType}
        />
      </FullMainHeight.Content>
    </FullMainHeight.Root>
  );
}

export default IntercultureListPage;
